import { useQueryClient } from "@tanstack/react-query";
import { orderBy } from "lodash";
import { useContext } from "react";
import { HjorteviltJaktdataV1JaktdagSharedJaktdagResponse } from "~/src/api/generated";
import { Individ, IndividResponse, Jaktdag, JaktdagResponse } from "~/src/api/types";
import { jaktdataApi } from "~/src/appGlobals";
import { hentJaktaar } from "~/src/helpers/time";
import { ArtType } from "~/src/helpers/types";
import AppDataContext, { AppData } from "../AppDataContext/DataProvider";
import { StorageContext, StorageItem } from "./StorageProvider";

export default function useJaktdataHelper() {
  const queryClient = useQueryClient();
  const storage = useContext(StorageContext);
  const jaktaar = hentJaktaar();
  const defaultAppData = useContext(AppDataContext);
  const REMOVE_UNCONFIRMED_DELETES_TIME = 10 * 60 * 1000; //kept for 10 minutes

  async function deleteSwCacheEntriesContaining(partialPath: string) {
    if (!navigator.onLine) return;
    try {
      const keys = await caches.keys();
      for (const key of keys) {
        if (!key.startsWith("settogskutt")) continue;
        const cache = await caches.open(key);
        const allKeys = await cache.keys();
        await Promise.all(
          allKeys.filter((req) => req.url.includes(partialPath)).map((req) => cache.delete(req)),
        );
      }
    } catch {}
  }

  function refetchAllJaktdata(specificAppData?: AppData) {
    const appData = specificAppData ?? defaultAppData;
    refetchAllJaktdager(appData);
    refetchAllIndivider(appData);

    const now = new Date().getTime();
    const confirmedDeletedJaktdager = storage
      .getAll<Jaktdag>("Jaktdag")
      .filter(
        (i) =>
          i.syncStatus === "unconfirmed" &&
          i.operation === "delete" &&
          i.lastUpdated &&
          Math.abs(now - i.lastUpdated?.getTime()) > REMOVE_UNCONFIRMED_DELETES_TIME,
      );
    confirmedDeletedJaktdager.forEach((j) => storage.delete(j.objectKey, "Jaktdag"));

    const confirmedDeletedIndivider = storage
      .getAll<Individ>("Individ")
      .filter(
        (i) =>
          i.syncStatus === "unconfirmed" &&
          i.operation === "delete" &&
          i.lastUpdated &&
          Math.abs(now - i.lastUpdated?.getTime()) > REMOVE_UNCONFIRMED_DELETES_TIME,
      );
    confirmedDeletedIndivider.forEach((j) => storage.delete(j.objectKey, "Individ"));
  }

  async function refetchJaktdataForJaktomrade(jaktomradeId: string) {
    await refetchJaktdagerForJaktfelt(jaktomradeId);
    await refetchIndividerForJaktomrade(jaktomradeId);
  }

  function refetchAllIndivider(appData: AppData) {
    appData.jaktområder?.data?.forEach((favOmrade) => {
      favOmrade.Områdekode && refetchIndividerForJaktomrade(favOmrade.Områdekode);
    });
  }

  function refetchAllJaktdager(appData: AppData) {
    appData.jaktområder?.data?.forEach((favOmrade) => {
      favOmrade.Områdekode &&
        favOmrade.Områdetype === "Jaktfelt" &&
        refetchJaktdagerForJaktfelt(favOmrade.Områdekode);
    });
  }

  async function refetchJaktdagerForJaktfelt(jaktomradeId: string) {
    try {
      await deleteSwCacheEntriesContaining(`/jaktdager/${jaktomradeId}/`);
      const jaktdager = await jaktdataApi.getJaktdager(jaktaar, jaktomradeId);
      queryClient.setQueryData(
        [
          "jaktdager",
          {
            jaktomradeId: jaktomradeId,
            sesong: hentJaktaar(),
          },
        ],
        jaktdager.map((jaktdag): StorageItem<JaktdagResponse> => {
          return {
            objectKey: { id: jaktdag.Id },
            isPending: false,
            data: jaktdag,
          };
        }),
      );

      await Promise.all(
        jaktdager.map(async (jaktdag) => {
          try {
            const j = await jaktdataApi.getJaktdag(jaktdag.Id || "");
            queryClient.setQueryData(["jaktdagId-" + j.Id, { jaktdagId: j.Id }], j);

            //remove any unconfirmed storage items that match this jaktdag, since they are now confirmed
            const inStore = storage.get<Jaktdag>({ id: jaktdag.Id }, "Jaktdag");
            if (inStore && inStore.syncStatus === "unconfirmed" && inStore.operation != "delete") {
              storage.delete({ id: jaktdag.Id }, "Jaktdag");
            }
          } catch (_error) {}
        }),
      );
    } catch (_error) {}
  }

  async function refetchIndividerForJaktomrade(jaktomradeId: string) {
    try {
      await deleteSwCacheEntriesContaining(`/individer/${jaktomradeId}/`);
      const individer = await jaktdataApi.getSkutteDyr(jaktaar, jaktomradeId);
      queryClient.setQueryData(
        [
          "skuttedyr",
          {
            jaktomradeId: jaktomradeId,
            sesong: hentJaktaar(),
          },
        ],
        individer.map((skuttDyr): StorageItem<IndividResponse> => {
          return {
            objectKey: { id: skuttDyr.Id },
            isPending: false,
            data: skuttDyr,
          };
        }),
      );
      await Promise.all(
        individer.map(async (individ) => {
          try {
            const i = await jaktdataApi.getIndivid(individ.Id || "");

            queryClient.setQueryData(["individId-" + i.Id, { individId: i.Id }], i);
            //remove any unconfirmed storage items that match this individ, since they are now confirmed
            const inStore = storage.get<Individ>({ id: individ.Id }, "Individ");
            if (inStore && inStore.syncStatus === "unconfirmed" && inStore.operation != "delete") {
              storage.delete({ id: individ.Id }, "Individ");
            }
          } catch (_error) {}
        }),
      );
    } catch (_error) {}
  }

  //Takes jaktdager from a fetch, then adds entries from store to return a list of jaktdager (not deleted/moved items) for the user to see
  //also removes confirmed items from store
  function updateJaktdagerFromStore(
    fetchedJaktdager: StorageItem<JaktdagResponse>[],
    jaktomradeId?: string,
  ) {
    // get stored items
    var storedItems = storage
      .getAll<JaktdagResponse>("Jaktdag")
      .filter((jaktdag) => jaktdag.data.Jaktfelt === jaktomradeId);
    const createdItems = storedItems.filter((item) => item.operation === "create");
    const updatedItems = storedItems.filter((item) => item.operation === "update");
    const deletedItems = storedItems.filter((item) => item.operation === "delete");

    // check if any jaktdager from the query have been moved to another jaktfelt offline
    const movedItems = storage
      .getAll<JaktdagResponse>("Jaktdag")
      .filter(
        (item) =>
          item.operation === "update" &&
          fetchedJaktdager.find(
            (j) => j.data.Id == item.data.Id && j.data.Jaktfelt != item.data.Jaktfelt,
          ),
      );

    // map individer to offline updated jaktdag
    updatedItems.forEach((updatedItem) => {
      const existingItem = fetchedJaktdager.find((j) => j.data.Id === updatedItem.data.Id);
      if (!existingItem) return;
      updatedItem.data.Individer = existingItem.data.Individer;
    });

    const fetchedJaktdagerToShow = fetchedJaktdager.filter((item) =>
      excludeFilters(item, [deletedItems, movedItems, updatedItems, createdItems]),
    );

    const displayedItems = createdItems
      .concat(updatedItems)
      .concat(fetchedJaktdagerToShow)
      .concat(deletedItems.filter((deletedItem) => !!deletedItem.errors));

    // set individer that might be in storage for each jaktdag
    displayedItems.forEach((item) => {
      storage.setIndividerForJaktdag(item.data as Jaktdag);
    });

    return orderBy(displayedItems, ["data.Dato"], ["desc"]);
  }

  function updateIndividerFromStore(
    fetchedIndivider: StorageItem<IndividResponse>[],
    jaktomradeId?: string,
  ) {
    const jegerNr = defaultAppData.jegeropplysninger?.data?.Jegernummer;

    const storedIndivider = storage
      .getAll<Individ>("Individ")
      .filter((individ) =>
        [individ.data.Jaktfelt, individ.data.Vald, individ.data.Villreinvald].includes(
          jaktomradeId,
        ),
      );
    const createdItems = storedIndivider.filter((item) => item.operation === "create");
    const updatedItems = storedIndivider.filter((item) => item.operation === "update");

    const deletedJaktdagerIds = storage
      .getAll<Jaktdag>("Jaktdag")
      .filter((jaktdag) => jaktdag.operation === "delete" && !jaktdag.errors)
      .map((jaktdag) => jaktdag.data.Id);
    const deletedItems = storedIndivider
      .filter((item) => item.operation === "delete")
      // individer connected to deleted jaktdager should not be shown
      .concat(
        (fetchedIndivider as StorageItem<Individ>[]).filter((item) =>
          deletedJaktdagerIds.includes(item.data.JaktdagId || ""),
        ),
      );

    // check if any individer from the query have been moved to another area offline
    const movedJaktomradeItems = storage
      .getAll<IndividResponse>("Individ")
      .filter(
        (item) =>
          item.operation === "update" &&
          fetchedIndivider.find(
            (j) =>
              j.data.Id == item.data.Id &&
              (j.data.Jaktfelt !== item.data.Jaktfelt ||
                j.data.Vald !== item.data.Jaktfelt ||
                j.data.Villreinvald !== item.data.Villreinvald),
          ),
      );

    // show individer from fetch that have not been deleted, moved, or updated offline
    const fetchedIndividerToShow = (fetchedIndivider as StorageItem<Individ>[]).filter(
      (fetchedIndivid) =>
        excludeFilters(fetchedIndivid, [
          deletedItems,
          movedJaktomradeItems,
          updatedItems,
          createdItems,
        ]),
    );

    const displayedItems = createdItems
      .concat(updatedItems)
      .concat(fetchedIndividerToShow)
      .concat(deletedItems.filter((deletedItem) => !!deletedItem.errors))
      .filter(
        (individ) => individ.data.Art != ArtType.Villrein || individ.data.Jegernummer === jegerNr,
      );
    return orderBy(displayedItems, ["data.Dato"], ["desc"]);
  }

  function excludeFilters(
    item: StorageItem<HjorteviltJaktdataV1JaktdagSharedJaktdagResponse>,
    lists: StorageItem<HjorteviltJaktdataV1JaktdagSharedJaktdagResponse>[][],
  ) {
    // exclude items that are in updatedItems, deletedItems or updatedJaktomradeItems
    // these items should not be shown, since the local offline version is showed
    const itemId = item.data.Id;
    return !lists.some((list) => list.some((u) => u.data.Id === itemId));
  }
  return {
    refetchAllJaktdata,
    refetchAllJaktdager,
    refetchJaktdataForJaktomrade,
    refetchJaktdagerForJaktfelt,
    refetchIndividerForJaktomrade,
    updateJaktdagerFromStore,
    updateIndividerFromStore,
  };
}
